import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Ban, CalendarClock, ChefHat, CheckCircle2, ClipboardCheck, Package, PenLine, Snowflake, Tag, Truck } from "lucide-react";
import api from "../../lib/api";
import { Card, Button, Spinner } from "../../components/ui-kit";
import { SingleRegistration } from "./ConformityRegistry";

const ICONS = {
  "controlli-oggi": ClipboardCheck,
  "controlli-periodici": CalendarClock,
  "non-conformita": AlertTriangle,
  "ricette-bozza": ChefHat,
  "materie-prime-senza-fornitore": Truck,
  "lotti-senza-fornitore": Package,
  "abbattimenti-in-attesa": Snowflake,
  "ricette-senza-conservazione": Tag,
  "ricette-lotti-mancanti": Ban,
};

const COLOR_STYLES = {
  amber: { border: "border-l-amber-500", bg: "bg-amber-50/60", icon: "bg-amber-100 text-amber-700", badge: "bg-amber-600" },
  red: { border: "border-l-red-500", bg: "bg-red-50/60", icon: "bg-red-100 text-red-700", badge: "bg-red-600" },
  rose: { border: "border-l-rose-500", bg: "bg-rose-50/60", icon: "bg-rose-100 text-rose-700", badge: "bg-rose-600" },
  violet: { border: "border-l-violet-500", bg: "bg-violet-50/60", icon: "bg-violet-100 text-violet-700", badge: "bg-violet-600" },
};

const todayKey = () => { const date = new Date(); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; };
const shortDate = (key) => (key ? `${key.slice(8, 10)}/${key.slice(5, 7)}` : "");

// Controllo settimanale/mensile del giorno: si dichiara qui, senza cambiare pagina.
function PeriodicItem({ item, open, onOpen, onClose, onDone }) {
  const today = todayKey();
  const kind = item.period === "week" ? "Settimanale" : "Mensile";
  const when = item.scheduled_day < today ? `previsto il ${shortDate(item.scheduled_day)}, ancora da fare` : "previsto per oggi";
  const where = [item.site_name, item.department_name].filter(Boolean).join(" · ");
  return (
    <div className="rounded-xl border border-amber-200 bg-white p-3 sm:p-4" data-testid={`checkup-periodic-${item.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-heading font-semibold text-navy">{item.name}</h4>
            {item.is_ccp && <span className="rounded bg-navy px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-bright">CCP</span>}
          </div>
          <p className={`mt-0.5 text-sm font-semibold ${item.scheduled_day < today ? "text-red-700" : "text-amber-700"}`}>{kind} · {when}</p>
          {where && <p className="mt-0.5 text-xs text-slate-500">{where}</p>}
          {item.instructions && <p className="mt-1 text-sm text-slate-600">Indicazioni: {item.instructions}</p>}
        </div>
        {!open && <Button className="w-full shrink-0 sm:w-auto" onClick={onOpen} data-testid={`checkup-register-${item.id}`}><PenLine className="h-4 w-4" />Registra</Button>}
      </div>
      {open && <SingleRegistration procedure={item} selectedDate={today} onDone={onDone} onCancel={onClose} />}
    </div>
  );
}

export default function CheckUp() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [openItem, setOpenItem] = useState(null);

  const load = useCallback(() => api.get("/app/checkup").then(({ data }) => setData(data)).catch(() => setData({ groups: [], total: 0 })), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="checkup-page">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Panoramica</p>
        <h1 className="font-heading text-3xl font-bold">Check-up</h1>
        <p className="mt-1 text-sm text-slate-500">Il quadro generale di tutto ciò che manca per essere completi e in regola: non solo i controlli di oggi, anche le configurazioni ancora da terminare.</p>
      </div>

      {!data ? <Spinner /> : data.total === 0 ? (
        <Card className="p-10 text-center" data-testid="checkup-all-clear">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h2 className="mt-4 font-heading text-xl font-semibold text-navy">Tutto in regola</h2>
          <p className="mt-1 text-sm text-slate-500">Nessuna mancanza rilevata al momento. Ottimo lavoro.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="checkup-groups">
          {data.groups.map((g) => {
            const style = COLOR_STYLES[g.color] || COLOR_STYLES.amber;
            const Icon = ICONS[g.id] || AlertTriangle;
            return (
              <Card key={g.id} className={`border-l-4 ${style.border} ${style.bg} p-4 sm:p-5`} data-testid={`checkup-${g.id}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${style.icon}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-heading font-semibold text-navy">{g.title}</h3>
                        <span className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white ${style.badge}`}>{g.count}</span>
                      </div>
                      <p className="mt-1 text-sm text-slate-600">{g.message}</p>
                    </div>
                  </div>
                  {!g.items && <Button variant="outline" className="w-full shrink-0 sm:w-auto" onClick={() => nav(g.link)} data-testid={`checkup-goto-${g.id}`}>
                    Vai a sistemare <ArrowRight className="h-4 w-4" />
                  </Button>}
                </div>
                {g.items && (
                  <div className="mt-4 space-y-3">
                    {g.items.map((item) => (
                      <PeriodicItem key={item.id} item={item} open={openItem === item.id}
                        onOpen={() => setOpenItem(item.id)} onClose={() => setOpenItem(null)}
                        onDone={async () => { setOpenItem(null); await load(); }} />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
